import React, { useState, useCallback, useEffect } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useTheme,
  Typography,
  Divider,
  SxProps,
  Theme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import PhoneIcon from "@mui/icons-material/Phone";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import logo from "../../assets/logo.png";
import { companyInfoData } from "../../services/apiService";

type HeaderVariant = "default" | "transparent" | "colored";

export interface HeaderProps {
  variant?: HeaderVariant;
  sx?: SxProps<Theme>;
  children?: React.ReactNode;
}

const navItems = [
  { name: "Home", path: "/" },
  { name: "Products", path: "/products" },
  { name: "Blog", path: "/blog" },
  { name: "About", path: "/about" },
  { name: "Contact", path: "/contact" },
];

const isNavItemActive = (path: string, pathname: string): boolean =>
  path === "/" ? pathname === "/" : pathname.startsWith(path);

const navButtonStyles = (theme: Theme): SxProps<Theme> => ({
  margin: theme.spacing(0, 1),
  borderRadius: 0,
  textTransform: "none",
  fontWeight: 500,
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },
});

const Header: React.FC<HeaderProps> = ({
  variant = "default",
  sx,
  children,
}) => {
  const theme = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const getAppBarStyles = (): SxProps<Theme> => {
    switch (variant) {
      case "transparent":
        return {
          backgroundColor: "transparent",
          boxShadow: "none",
          color: theme.palette.common.white,
        };
      case "colored":
        return {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
        };
      default:
        return {
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderBottom: `1px solid ${theme.palette.divider}`,
        };
    }
  };

  const getButtonStyles = (isActive: boolean): SxProps<Theme> => {
    const baseStyles = {
      "&.MuiButton-root": {
        color: variant === "colored" ? theme.palette.common.white : "inherit",
      },
      "&:hover": {
        backgroundColor:
          variant === "colored"
            ? "rgba(255, 255, 255, 0.1)"
            : "rgba(0, 0, 0, 0.04)",
      },
    };

    if (isActive) {
      return {
        ...baseStyles,
        "&.MuiButton-root": {
          ...baseStyles["&.MuiButton-root"],
          borderBottom: `2px solid ${
            variant === "colored"
              ? theme.palette.common.white
              : theme.palette.primary.main
          }`,
          borderRadius: 0,
        },
      };
    }
    return baseStyles;
  };

  // Use useCallback to prevent unnecessary re-renders
  const handleDrawerToggle = useCallback(() => {
    setMobileOpen((prev) => !prev);
  }, []);

  // Close drawer when location changes
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const whatsappDigits = (
    companyInfoData.contact.whatsapp ?? companyInfoData.contact.redirectPhone
  ).replace(/\D/g, "");

  const drawerCtaButtonStyles: SxProps<Theme> = {
    flex: 1,
    color: "common.white",
    backgroundColor: theme.custom.glass.background,
    border: theme.custom.glass.border,
    backdropFilter: theme.custom.glass.blur,
    px: 2,
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.16)",
    },
  };

  const drawer = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2.5,
          py: 2,
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: "1.05rem",
            letterSpacing: "-0.01em",
            color: "common.white",
          }}
        >
          R.S.{" "}
          <Box component="span" sx={{ color: "secondary.light" }}>
            Enterprises
          </Box>
        </Typography>
        <IconButton
          aria-label="close drawer"
          onClick={handleDrawerToggle}
          sx={{ color: "common.white" }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider sx={{ borderColor: "rgba(255,255,255,0.12)" }} />

      <List sx={{ flexGrow: 1, pt: 2 }}>
        {navItems.map((item) => {
          const isActive = isNavItemActive(item.path, location.pathname);
          return (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                component={RouterLink}
                to={item.path}
                selected={isActive}
                sx={{
                  mx: 1.5,
                  mb: 0.5,
                  py: 1.5,
                  px: 2.5,
                  borderRadius: "12px",
                  borderLeft: "3px solid transparent",
                  color: "rgba(255,255,255,0.85)",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.08)",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "rgba(199,93,44,0.16)",
                    borderLeft: `3px solid ${theme.palette.secondary.main}`,
                    color: "secondary.light",
                    "&:hover": {
                      backgroundColor: "rgba(199,93,44,0.24)",
                    },
                    "& .MuiListItemText-primary": {
                      fontWeight: 700,
                    },
                  },
                }}
              >
                <ListItemText
                  primary={item.name}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.12)" }} />
      <Box sx={{ display: "flex", gap: 1, p: 2 }}>
        <Button
          href={`tel:${companyInfoData.contact.redirectPhone}`}
          startIcon={<PhoneIcon />}
          sx={drawerCtaButtonStyles}
        >
          Call
        </Button>
        <Button
          href={`https://wa.me/${whatsappDigits}`}
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<WhatsAppIcon />}
          sx={drawerCtaButtonStyles}
        >
          WhatsApp
        </Button>
      </Box>
    </Box>
  );

  return (
    <AppBar
      position="fixed"
      color={variant === "transparent" ? "transparent" : "inherit"}
      elevation={0}
      sx={[getAppBarStyles(), ...(Array.isArray(sx) ? sx : [sx])]}
    >
      <Container
        maxWidth={false}
        sx={{ maxWidth: theme.custom.maxWidth, px: { xs: 2, sm: 3, md: 4 } }}
      >
        <Toolbar
          disableGutters
          sx={{ display: "flex", justifyContent: "space-between" }}
        >
          <Box
            component={RouterLink}
            to="/"
            sx={{
              textDecoration: "none",
              display: "flex",
              alignItems: "center",
              gap: 2,
              "&:hover": {
                opacity: 0.8,
              },
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="R.S. Enterprises"
              title="R.S. Enterprises"
              sx={{
                height: 40,
                width: "auto",
                display: "block",
                objectFit: "contain",
                // Remove left margin to align with container
                ml: { xs: -2, sm: -3, md: -4 }, // Negative margin to align with container's padding
                "@media (min-width: 1200px)": {
                  ml: 0, // Reset margin on extra large screens if needed
                },
              }}
            />
          </Box>

          <Box sx={{ display: { xs: "none", md: "block" } }}>
            {navItems.map((item) => {
              const isActive = isNavItemActive(item.path, location.pathname);
              return (
                <Button
                  key={item.path}
                  component={RouterLink}
                  to={item.path}
                  sx={(theme) => ({
                    ...navButtonStyles(theme),
                    ...(getButtonStyles(isActive) as object),
                  })}
                >
                  {item.name}
                </Button>
              );
            })}
          </Box>

          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              display: { md: "none" },
              color:
                variant === "colored" || variant === "transparent"
                  ? "common.white"
                  : "inherit",
            }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </Container>

      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: false,
          disableScrollLock: true,
          disableEscapeKeyDown: false,
          BackdropProps: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              backdropFilter: "blur(4px)",
            },
          },
        }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: "min(85vw, 320px)",
            border: "none",
            background: theme.custom.gradients.hero,
            color: theme.palette.common.white,
          },
        }}
      >
        {drawer}
      </Drawer>
    </AppBar>
  );
};

export default Header;
